import { NoImage } from "@assets/constant/index.constant";
import { CloudUpload } from "@assets/icons";
import { Badge } from "@components/ui/badge";
import { endpoint } from "@services/api.services";
import { UseMutateFunction } from "@tanstack/react-query";
import { UserProfile } from "@ui/internal/components/card";
import { ZoomImage } from "@ui/internal/components/formelement";
import PrintCertificate from "@ui/internal/components/print-certificate";
import { isNullCheck, showToast } from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { useSelectionStore } from "@utils/states/index.state";
import { AxiosResponse } from "axios";
import { ClipboardCopy, DeleteIcon, PlusCircle, TrashIcon } from "lucide-react";
import { useMemo } from "react";
import { NavLink } from "react-router";
interface CloumnListProps {
  refetch?: () => void;
  deleteGlimpsImage?: UseMutateFunction<
    AxiosResponse<any, any>,
    any,
    number,
    unknown
  >;
  DeletePending?: boolean;
  onSendEmail?: (row: any) => void;
  sendingEmailIds?: Set<number | string>;
  sentEmailIds?: Set<number>;
}
const ColumnList = ({ refetch, deleteGlimpsImage, onSendEmail, sendingEmailIds, sentEmailIds }: CloumnListProps) => {
  const CertificateEndpoint = useMemo(() => endpoint["certificate"], []);
  const { clearSelection } = useSelectionStore();
  const { handleAction } = useHandleAction({
    endpoint: CertificateEndpoint,
    refetch,
  });
  const columns = useMemo(
    () => [
      {
        name: "ID",

        cell: (row: any) => (
          <span className="font-semibold text-gray-700">{row?.id}</span>
        ),
        sortable: true,
      },
      {
        name: "Certificate Number",
        cell: (row: any) => (
          <>
            <div className="flex space-x-2 gap-2 items-center">
              {isNullCheck(row?.certificate_number) as string}
              <ClipboardCopy
                onClick={() => {
                  window.navigator.clipboard.writeText(row?.certificate_number);
                  showToast({
                    type: "success",
                    description:
                      "Successfully copied " +
                      row?.certificate_number +
                      " into clipboard",
                    title: "Success",
                  });
                }}
                className="cursor-pointer"
                size={18}
              />{" "}
            </div>
          </>
        ),
        sortable: false,
      },
      {
        name: "Full Name",
        cell: (row: any) => (
          <>
            <UserProfile
              userimage={row?.student?.professional_image ?? NoImage}
              userdesignation={row?.student?.email}
              username={row?.student?.full_name}
            />
          </>
        ),
        sortable: false,
      },
      {
        name: "Course & Batch",
        cell: (row: any) => (
          <Badge
            variant={"secondary"}
            className="w-fit text-[#f0af1d] capitalize"
          >
            {isNullCheck(row?.course_batch?.name) as string}
          </Badge>
        ),
        sortable: true,
      },
      {
        name: "Glimpses",
        cell: (row: any) => (
          <>
            {row?.glimpse ? (
              <>
                <ZoomImage>
                  <div className="flex border rounded-md hover:outline-[#1aa178]/50 hover:outline-2 overflow-hiddenbg-slate-100">
                    <img
                      src={row?.glimpse || NoImage}
                      alt="Cropped"
                      onError={(e: any) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = NoImage;
                      }}
                      className="object-cover h-[80px] max-md:h-[40px] max-md:w-[40px] w-[100px] border rounded-md"
                    />
                  </div>
                </ZoomImage>
                <TrashIcon
                  color="black"
                  className="h-4 cursor-pointer"
                  onClick={() =>
                    row.id != undefined && deleteGlimpsImage
                      ? deleteGlimpsImage(row.id)
                      : null
                  }
                />
              </>
            ) : (
              <NavLink
                to={`/certificate/certificates?modal=edit&modal-item=${row.id}#certificate-glimpse`}
              >
                <div
                  className="flex gap-2 cursor-pointer"
                  onClick={() =>
                    handleAction({ type: "modal-edit", data: row?.id })
                  }
                >
                  <CloudUpload size={16} className={"h-[20px] w-[20px]"} />{" "}
                  Upload Glimpse
                </div>
              </NavLink>
            )}
          </>
        ),
        sortable: true,
      },
      {
        name: "Remarks",
        cell: (row: any) => (
          <>
            <NavLink
              to={`/certificate/certificates?modal=edit&modal-item=${row.id}#certificate-remarks`}
            >
              {row?.remarks ? (
                <div className="overflow-ellipsis h-[50px] w-[150px]">
                  {row?.remarks}
                </div>
              ) : (
                <div className="flex gap-2 items-center">
                  <PlusCircle size={16} /> Add Remarks
                </div>
              )}
            </NavLink>
          </>
        ),
        sortable: true,
      },

      {
        name: "Email Log",
        cell: (row: any) => {
          const isSent = !!row?.email_sent_at || sentEmailIds?.has(row?.id);
          const isSending = sendingEmailIds?.has(row?.id);
          const sentAt = row?.email_sent_at || (sentEmailIds?.has(row?.id) ? new Date().toISOString() : null);
          return (
            <div className="flex flex-col gap-1">
              {isSent && sentAt && (
                <span className="text-xs text-gray-500">
                  {new Date(sentAt).toLocaleString("en-US", {
                    hour12: true,
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
              <div className="flex items-center gap-2">
                {isSent ? (
                  <button
                    className="text-[#1DA077] text-xs font-medium underline cursor-pointer"
                    onClick={() => onSendEmail && onSendEmail(row)}
                    disabled={isSending}
                  >
                    {isSending ? "Sending..." : "Resend"}
                  </button>
                ) : (
                  <button
                    className="text-[#C33241] text-xs font-medium underline cursor-pointer"
                    onClick={() => onSendEmail && onSendEmail(row)}
                    disabled={isSending}
                  >
                    {isSending ? "Sending..." : "Send"}
                  </button>
                )}
              </div>
            </div>
          );
        },
        sortable: false,
      },
      {
        name: "Actions",
        cell: (row: any) => (
          <div className="flex items-center gap-3 cursor-pointer font-[500] select-none">
            <button
              className="text-[#1DA077] cursor-pointer"
              onClick={() => handleAction({ type: "modal-edit", data: row.id })}
              title="Edit"
            >
              Edit
            </button>
            <button
              className="text-[#C33241] cursor-pointer"
              onClick={() => {
                clearSelection();
                handleAction({
                  type: "confirm",
                  data: { type: "delete", id: row.id },
                });
              }}
              title="Delete"
            >
              Delete
            </button>
            <PrintCertificate certificateId={row.id} />
          </div>
        ),
        sortable: false,
      },
    ],
    [handleAction, clearSelection, onSendEmail, sendingEmailIds, sentEmailIds]
  );

  return columns;
};

const TemplateColumnList = ({ refetch }: CloumnListProps) => {
  const TemplateEndpoint = useMemo(() => endpoint["templates"], []);
  const { clearSelection } = useSelectionStore();
  const { handleAction } = useHandleAction({
    endpoint: TemplateEndpoint,
    refetch,
  });
  const columns = useMemo(
    () => [
      {
        name: "ID",

        cell: (row: any) => (
          <span className="font-semibold text-gray-700">{row?.id}</span>
        ),
        sortable: true,
      },
      {
        name: "Title",
        cell: (row: any) => <>{row.title}</>,
        sortable: false,
      },

      {
        name: "Actions",
        cell: (row: any) => (
          <div className="flex items-center gap-3 cursor-pointer font-[500] select-none">
            <button
              className="text-[#1DA077] cursor-pointer"
              onClick={() => handleAction({ type: "page-edit", data: row.id })}
              title="Edit"
            >
              Edit
            </button>
            <button
              className="text-[#C33241] cursor-pointer"
              onClick={() => {
                clearSelection();
                handleAction({
                  type: "confirm",
                  data: { type: "delete", id: row.id },
                });
              }}
              title="Delete"
            >
              Delete
            </button>
          </div>
        ),
        sortable: false,
      },
    ],
    [handleAction, clearSelection]
  );

  return columns;
};

export { ColumnList, TemplateColumnList };
